import { useDraggable } from '@dnd-kit/core';
import { AnimatePresence } from 'framer-motion';
import styled from 'styled-components';

import { IconIndicator } from '@components/icons/indicator';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { getScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';
import { transitionStandard, transitionSlowest } from '@utils/transitions';

import { FieldDropzone } from './field-dropzone';

import { conditionsTab } from '../conditions-tab';
import { useFormBuilderContext } from '../context';
import { SmallDragHandle } from '../drag-handle';
import { FieldEditContainer } from '../field-edit';
import { FieldEditHeader, FieldEditHeaderActions } from '../field-edit-header';
import { FieldConfig } from '../field-registry';
import { StaleConditionWarningIcon, useHasStaleConditionOption } from '../stale-condition-warning';
import { StateField } from '../state';

type Config = FieldConfig<'separator'>;

const FormBuilderSeparator: Config['Component'] = ({ field }) => {
  const {
    store: { actions, useIsFieldSelected },
  } = useFormBuilderContext();
  const isSelected = useIsFieldSelected(field.id);
  const hasStaleConditionWarning = useHasStaleConditionOption(field);

  const disallowDrag = field.builderProps?.canReorder === false;

  const draggable = useDraggable({
    id: `selected-${field.id}`,
    data: {
      field,
    },
    disabled: disallowDrag,
  });

  if (draggable.isDragging) {
    return null;
  }

  return (
    <FieldDropzone field={field}>
      <SeparatorContainer
        id={`field-${field.id}`}
        ref={draggable.setNodeRef}
        data-testid="formbuilder-separator-container"
        {...draggable.attributes}
        {...draggable.listeners}
        data-selected={isSelected}
        onClick={(e) => {
          e.stopPropagation();
          actions.selectField(field.id);
        }}
      >
        <AnimatePresence initial={false}>
          {isSelected ? (
            <FieldEditHeader
              dragHandle={<SmallDragHandle />}
              field={field}
              content={<DividerWithWarning field={field} hasWarning={hasStaleConditionWarning} />}
            />
          ) : (
            <Stack space={2} orientation="horizontal" align="center" justify="space-between">
              <div data-handle>
                <SmallDragHandle />
              </div>
              <DividerWithWarning field={field} hasWarning={hasStaleConditionWarning} />
              <FieldEditHeaderActions field={field} />
            </Stack>
          )}
        </AnimatePresence>
      </SeparatorContainer>
    </FieldDropzone>
  );
};

const { t } = getScopedTranslate({
  en: {
    title: 'Separator',
  },
});

export const separatorConfig: Config = {
  kind: 'separator',
  title: t('title'),
  group: 'structure',
  propertiesTabs: [conditionsTab],
  TreeDisplay: Separator,
  Display: Separator,
  Component: FormBuilderSeparator,
  Icon: IconIndicator,
};

// In the row's middle slot, not a corner: the drag handle and hover actions claim
// both ends, and a corner badge that hides for them puts the tooltip out of reach
// on the one kind with no label to fall back on.
const DividerRow = styled(Stack).attrs({
  orientation: 'horizontal' as const,
  align: 'center',
  space: 2 as const,
})({
  flex: 1,
  minWidth: 0,
});

const DividerWithWarning = ({ field, hasWarning }: { field: StateField; hasWarning: boolean }) => (
  <DividerRow>
    <Separator />
    {hasWarning && <StaleConditionWarningIcon field={field} />}
  </DividerRow>
);

const SeparatorContainer = styled(FieldEditContainer)({
  transition: transitionStandard('background'),
  overflow: 'hidden',

  '[data-handle="true"]': {
    transition: transitionSlowest('all'),
    visibility: 'hidden',
    margin: `-${pxToRem(4)}`,
    marginLeft: '-50%',
    marginRight: '10%',
    opacity: 0,
  },

  '[data-c="edit-header-actions"]': {
    transition: transitionSlowest('all'),
    visibility: 'hidden',
    margin: `-${pxToRem(4)}`,
    marginRight: '-50%',
    opacity: 0,
  },

  '&:hover': {
    '[data-c="edit-header-actions"]': {
      visibility: 'visible',
      transition: transitionStandard('all'),
      marginRight: 0,
      opacity: 1,
    },

    '[data-handle="true"]': {
      visibility: 'visible',
      transition: transitionStandard('all'),
      marginLeft: 0,
      marginRight: 0,
      opacity: 1,
    },
  },

  '&[data-selected="true"]': {
    padding: `${pxToRem(3)} ${pxToRem(7)}`,
    '[data-c="edit-header-actions"]': {
      margin: 0,
      visibility: 'visible',
      opacity: 1,
    },
  },
});
